import Sentry from "@similarweb/sentry-logger";

import type { ISwSettings } from "app/@types/ISwSettings";
import { TEAMS } from "app/constants/teamName";
import * as usersUtils from "app/UtilitiesAndConstants/UtilityFunctions/usersUtils";

import type { Tests, TestValues } from "./types";

const CLIENT_KEYS = {
    production: "sdk-KizVsnJLvGSg8HZ",
    staging: "sdk-HC8TAsf5GLgaMqZ",
};

export const getClientKey = (): string => {
    return CLIENT_KEYS[window.location.host?.includes("sandbox") ? "staging" : "production"];
};

export const getUserType = (swSettings: ISwSettings) => ({
    // 1. Base values
    isTrial: usersUtils.isTraditionalTrialUser(swSettings),
    isAfterTrial: usersUtils.isAfterTrial(swSettings),
    isNT: usersUtils.isNoTouch(swSettings),
    isSI: usersUtils.isSI(swSettings),
    hasUserAddon: usersUtils.hasUserAddon(swSettings),
    hasHistoricalDataAddon: usersUtils.hasHistoricalDataAddon(swSettings),
    isCCUserInTrial: usersUtils.isCCTrialUser(swSettings),
    isAfterTrialCC: usersUtils.isSkippedCC(swSettings),
    isMonthlyPaymentPeriod: usersUtils.isMonthlyPaymentPeriod(swSettings),

    // 2. First-level
    isTrialOrAfterTrial: usersUtils.isTraditionalTrialOrAfterTrialUser(swSettings),

    // 3. Second-level
    isDMITrialOrAfterTrial: usersUtils.isDMITrialOrAfterTrial(swSettings),
    isDMITrial: usersUtils.isDMITrial(swSettings),
    isDMIAfterTrial: usersUtils.isDMIAfterTrial(swSettings),
    isSITrial: usersUtils.isSITrial(swSettings),
    isSIAfterTrial: usersUtils.isSIAfterTrial(swSettings),
    hasAddonsEnabled: usersUtils.hasAddonsEnabled(swSettings),
    isCCUserOrReactivatedInTrial: usersUtils.isCCTrialUser(swSettings), //TODO check what function should be used here
    hasNTFlow: usersUtils.getHasNTFlow(swSettings),
    hasAccessToActivationHomepage2: usersUtils.hasAccessToActivationHomepage2(swSettings),
    hasAccessToOnboardingBot: usersUtils.hasAccessToOnboardingBot(swSettings),
});

/**
 * Transforms the global `gbTestsNames` object into a format suitable for tracking page views.
 *
 * @returns {Object} An object containing two arrays:
 * - `ab_test_name` (Array<string>): The keys from the `gbTests` object.
 * - `ab_test_value` (Array<string>): The corresponding variation or control values.
 */
/* eslint-disable @typescript-eslint/naming-convention */
export const pageViewTrackingUtility = (
    gbTests: Partial<Tests>,
): { ab_test_name: string; ab_test_value: string } | null => {
    const abTestNames: string[] = [];
    const abTestValues: TestValues[] = [];

    if (Object.keys(gbTests).length) {
        Object.entries(gbTests).forEach(([testName, testVariant]) => {
            abTestNames.push(`GB-${testName}`);
            abTestValues.push(testVariant);
        });
    }

    return abTestNames.length && abTestValues.length
        ? {
              ab_test_name: abTestNames.join(","),
              ab_test_value: abTestValues.join(","),
          }
        : null;
};

const trackExperimentEvent = (testName: string, testVariant: TestValues) => {
    if (!window.mixpanel) {
        return;
    }

    console.debug(
        `%c Track Event: Growthbook Test, Test Name: ${testName}, Variant: ${testVariant}.`,
        "background: #222; color: #bada55",
    );

    window.mixpanel.track("$experiment_started", {
        "Experiment name": testName,
        "Variant name": testVariant,
        $source: "growthbook",
    });
};

/**
 * Sends test experiment data to Mixpanel.
 */
export const mixpanelTracking = (gbTests: Partial<Tests>) => {
    if (!gbTests) {
        return;
    }

    const callback = () => {
        if (!Object.keys(gbTests).length) {
            return;
        }

        Object.entries(gbTests).forEach(([testName, testVariant]) => {
            trackExperimentEvent(testName, testVariant);
        });

        $(document).off("MixpanelReady", callback);
    };
    try {
        if (window.mixpanel?.track) {
            callback();
        } else {
            $(document).on("MixpanelReady", callback);
        }
    } catch (ex) {
        Sentry.errorTracker({
            message: "[PRO] - Growthbook - Error in mixpanelTracking",
            e: ex,
            level: "error",
            params: {
                tags: { team: TEAMS.GROWTH },
            },
        });
    }
};
